import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import moment from "moment";
import { v4 } from "uuid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faBan,
  faCheckCircle,
  faClock,
  faEnvelopeOpenText,
  faFileAlt,
  faPaperclip,
  faUniversity,
  faUpload,
  faUser,
} from "@fortawesome/free-solid-svg-icons";
import {
  getRequest,
  getAllRequests,
  apiRejectFinance,
  apiApproveFinance,
  apiResubmitFinance,
  getMyHRStatus,
} from "../../../utils/queries";
import { trackPromise } from "react-promise-tracker";
import { authProvider } from "../../../authProvider";
import { ResubmitModal } from "../HomePage/ResubmitModal";
import { RejectModal } from "../HomePage/RejectModal";
import { ApproveModal } from "../HomePage/ApproveModal";
import { FilesForm } from "../HomePage/Forms/FilesForm";
import Alerts from "../../molecules/Alerts";
import "./FinanceDetailsPage.css";

const paymentTypes = {
  IBAN: "IBAN",
  SWIFT: "BIC",
  REGNOACCOUNT: "REGNO/ACCOUNT",
  ABARoutingNumber: "ABA ROUTING NUMBER",
};

const statusColorMap = {
  "Completed": "rdp-status--approved",
  "Approved by line manager": "rdp-status--approved",
  "Approved by finance": "rdp-status--approved",
  "Validated by requestor": "rdp-status--resubmitted",
  "Form requested": "rdp-status--requested",
  "Send back by requestor": "rdp-status--sendback",
  "Cancelled": "rdp-status--rejected",
  "Rejected by line manager": "rdp-status--rejected",
  "Rejected by finance": "rdp-status--rejected",
  "Form resubmitted": "rdp-status--resubmitted",
};

const SectionCard = ({ icon, title, badge, children }) => (
  <div className="rdp-section-card">
    <div className="rdp-section-card__header">
      <div className="rdp-section-card__title">
        {icon && <FontAwesomeIcon icon={icon} />}
        <span>{title}</span>
      </div>
      {badge != null && <span className="rdp-section-card__badge">{badge}</span>}
    </div>
    <div className="rdp-section-card__body">{children}</div>
  </div>
);

const bField = (label, value) => (
  <div className="rdp-vbdm__field" key={label}>
    <div className="rdp-vbdm__field-label">{label}</div>
    <div className={`rdp-vbdm__field-value${!value ? " rdp-vbdm__field-value--empty" : ""}`}>
      {value || "—"}
    </div>
  </div>
);

const fmt = (date) => (date ? moment(date).format("DD-MM-YYYY HH:mm") : "-");

const getInitials = (name) => {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const getErrorMessage = (error, fallback) =>
  error?.response?.data?.message || error?.message || fallback;

const FinanceDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const userAccount = authProvider.account;

  const [request, setRequest] = useState(null);
  const [banks, setBanks] = useState(null);
  const [employee, setEmployee] = useState(null);
  const [myHRStatus, setMyHRStatus] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [fileList, setFileList] = useState([]);
  const [disableAllApprove, setDisableAllApprove] = useState(0);
  const [loading, setLoading] = useState(true);

  const [toggleResubmitModal, setToggleResubmitModal] = useState(false);
  const [toggleRejectModal, setToggleRejectModal] = useState(false);
  const [toggleApproveModal, setToggleApproveModal] = useState(false);

  const addAlert = (message, color = "success") => {
    setAlerts((prev) => [...prev, { message, id: v4(), color }]);
  };

  useEffect(() => {
    async function loadRequest() {
      try {
        setLoading(true);
        const result = await trackPromise(getRequest(id));
        const requestData = result.data.request || result.data.account || result.data;

        setEmployee(requestData.employee || result.data.employee);
        setRequest(requestData);
        setBanks(result.data.bankDataList);
        setFileList(result.data.files || []);
        checkRequesterAndLineManager(result.data.account);
      } catch (error) {
        addAlert(getErrorMessage(error, "Failed to load account details"), "error");
      } finally {
        setLoading(false);
      }
    }
    if (id) loadRequest();
  }, [id]);

  useEffect(() => {
    getAllRequests()
      .then((result) => {
        setDisableAllApprove(result.data?.disableAllApproval || 0);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (request?.id) {
      getMyHRStatus(request.id)
        .then((result) => setMyHRStatus(result.data))
        .catch(() => {});
    }
  }, [request]);

  const checkRequesterAndLineManager = (account) => {
    if (userAccount && account) {
      const isLineManager = userAccount?.userName === account?.lineManagerToNotify;
      const isRequester = userAccount?.userName === account?.requestedByEmail;
      if (isLineManager && isRequester) {
        addAlert(
          "Notice on this account you are a requester or have approved as line manager!",
          "warning"
        );
      }
    }
  };

  const canTakeAction = () => request?.status === "Approved by line manager";
  const canApprove = () => canTakeAction() && disableAllApprove !== 1;

  const onResubmit = async (requestId, reason) => {
    try {
      const result = await apiResubmitFinance(requestId, reason);
      setRequest(result.data);
      addAlert("Successfully resubmitted the request");
    } catch (error) {
      addAlert(getErrorMessage(error, "Failed to resubmit request"), "error");
    }
  };

  const onReject = async (requestId, reason) => {
    try {
      const result = await apiRejectFinance(requestId, reason);
      setRequest(result.data);
      addAlert("Request rejected successfully");
    } catch (error) {
      addAlert(getErrorMessage(error, "Failed to reject request"), "error");
    }
  };

  const onApprove = async (requestId) => {
    try {
      const result = await apiApproveFinance(requestId);
      setRequest(result.data);
      addAlert("Request approved successfully");
    } catch (error) {
      addAlert(getErrorMessage(error, "Failed to approve account"), "error");
    }
    return null;
  };

  const renderBankFields = (bankData) => {
    const type = bankData?.type;
    const accountNr = bankData?.accountNr ?? bankData?.bankAccountNumber;
    const currency = bankData?.currency?.currencyCode;
    let fields;

    if (type === paymentTypes.IBAN) {
      fields = [
        ["Beneficiary name", bankData?.beneficiaryBankAccountName],
        ["Currency", currency],
        ["IBAN", bankData?.iban],
        ["SWIFT", bankData?.bic],
        ["Bank", bankData?.bank],
        ["Branch", bankData?.branch],
        ["City", bankData?.city],
        ["Zip", bankData?.zip],
        ["Country", bankData?.country],
        ["Country Code", bankData?.countryCode],
      ];
    } else if (type === paymentTypes.SWIFT) {
      fields = [
        ["Beneficiary name", bankData?.beneficiaryBankAccountName],
        ["Currency", currency],
        ["Account number", accountNr],
        ["SWIFT", bankData?.bic],
        ["Corresponding SWIFT", bankData?.correspondingBIC],
        ...(bankData?.regNo ? [["Registration Number", bankData.regNo]] : []),
        ["Bank", bankData?.bank],
        ["Address", bankData?.address],
        ["City", bankData?.city],
        ["Zip", bankData?.zip],
        ["Country", bankData?.country],
        ["Country Code", bankData?.countryCode],
      ];
    } else if (type === paymentTypes.REGNOACCOUNT) {
      fields = [
        ["Registration Number", bankData?.regNo],
        ["Account number", accountNr],
        ["IBAN", bankData?.iban],
        ["Currency", currency || "DKK"],
        ["SWIFT", bankData?.bic],
        ["Bank", bankData?.bank],
        ["Address", bankData?.address],
        ["City", bankData?.city],
        ["Zip", bankData?.zip],
        ["Country", bankData?.country || "Denmark"],
      ];
    } else if (type === paymentTypes.ABARoutingNumber) {
      fields = [
        ["Account Type", bankData?.accountType],
        ["ABA Routing Number", bankData?.abaRoutingNumber],
        ["Bank Account Number", bankData?.bankAccountNumber],
        ["Beneficiary name", bankData?.beneficiaryBankAccountName],
        ["Bank", bankData?.bank],
        ["Branch", bankData?.branch],
        ["Address", bankData?.address],
        ["City", bankData?.city],
        ["Zip", bankData?.zip],
        ["Country", bankData?.country],
        ["Currency", currency],
      ];
    } else {
      fields = [
        ["Beneficiary name", bankData?.beneficiaryBankAccountName],
        ["Currency", currency],
        ["IBAN", bankData?.iban],
        ["SWIFT", bankData?.bic],
        ["Corresponding SWIFT", bankData?.correspondingBIC],
        ["Registration Number", bankData?.regNo],
        ["Account number", accountNr],
        ["ABA Routing Number", bankData?.abaRoutingNumber],
        ["Bank", bankData?.bank],
        ["Branch", bankData?.branch],
        ["Address", bankData?.address],
        ["City", bankData?.city],
        ["Zip", bankData?.zip],
        ["Country", bankData?.country],
        ["Country Code", bankData?.countryCode],
      ].filter(([, value]) => value);
    }

    return [["Type", type], ["Purpose", bankData?.purpose], ...fields].map(([label, value]) =>
      bField(label, value)
    );
  };

  if (loading) {
    return <div className="rdp-container" />;
  }

  if (!request) {
    return (
      <div className="rdp-container">
        <button className="rdp-back-btn" onClick={() => navigate("/requests")}>
          <FontAwesomeIcon icon={faArrowLeft} /> Back to Requests
        </button>
        <Alerts alerts={alerts} setAlerts={setAlerts} />
        <div className="rdp-loading">Request not found.</div>
      </div>
    );
  }

  // Approval fields are nested under `approval` in some responses and flat in others.
  const approval = request.approval || request;
  const heroCls = statusColorMap[request.status] || "rdp-status--default";
  const employeeName = `${employee?.firstName || ""} ${employee?.lastName || ""}`.trim();

  return (
    <div className="rdp-container">
      <button className="rdp-back-btn" onClick={() => navigate("/requests")}>
        <FontAwesomeIcon icon={faArrowLeft} /> Back to Requests
      </button>

      <Alerts alerts={alerts} setAlerts={setAlerts} />

      <div className="rdp-hero">
        <div className="rdp-hero__top">
          <div className="rdp-hero__identity">
            <div className="rdp-hero__avatar">{getInitials(employeeName)}</div>
            <div>
              <div className="rdp-hero__name">{employeeName || "-"}</div>
              <div className="rdp-hero__id">Request #{request.id}</div>
            </div>
          </div>
          <span className={"rdp-hero__status " + heroCls}>{request.status}</span>
        </div>

        <div className="rdp-hero__meta">
          <div className="rdp-hero__meta-item">
            <span className="rdp-hero__meta-label">Requestor</span>
            <span className="rdp-hero__meta-value">{request.requestedByName || "-"}</span>
          </div>
          <div className="rdp-hero__meta-item">
            <span className="rdp-hero__meta-label">Line Manager</span>
            <span className="rdp-hero__meta-value">{request.lineManagerToNotify || "-"}</span>
          </div>
          <div className="rdp-hero__meta-item">
            <span className="rdp-hero__meta-label">Finance Approver</span>
            <span className="rdp-hero__meta-value">{request.financeApproverToNotify || "-"}</span>
          </div>
          <div className="rdp-hero__meta-item">
            <span className="rdp-hero__meta-label">Submitted</span>
            <span className="rdp-hero__meta-value">{fmt(request.formSubmittedDate)}</span>
          </div>
        </div>

        <div className="rdp-hero__actions">
          <button
            className="rdp-btn rdp-btn--approve"
            onClick={() => onApprove(request.id)}
            disabled={!canApprove()}
          >
            <FontAwesomeIcon icon={faCheckCircle} /> Approve
          </button>
          <button
            className="rdp-btn rdp-btn--reject"
            onClick={() => setToggleRejectModal(true)}
            disabled={!canTakeAction()}
          >
            <FontAwesomeIcon icon={faBan} /> Reject
          </button>
          <button
            className="rdp-btn rdp-btn--resubmit"
            onClick={() => setToggleResubmitModal(true)}
            disabled={!canTakeAction()}
          >
            <FontAwesomeIcon icon={faEnvelopeOpenText} /> Send back to employee
          </button>
        </div>
      </div>

      <SectionCard icon={faUser} title="Employee Information">
        <div className="rdp-vbdm__body">
          <div className="rdp-vbdm__section">
            <div className="rdp-vbdm__grid">
              {bField("First name", employee?.firstName)}
              {bField("Last name", employee?.lastName)}
              {bField("Private email", employee?.privateEmail)}
              {bField("Private phone", employee?.privatePhone)}
              {bField("Address Line 1", employee?.addressLine1)}
              {bField("Address Line 2", employee?.addressLine2)}
              {bField("City", employee?.city)}
              {bField("Zip/Postal", employee?.zip)}
              {bField("Country", employee?.country)}
              {bField("MyHR Id", employee?.myHRId)}
            </div>
          </div>
        </div>
      </SectionCard>

      <SectionCard icon={faFileAlt} title="Request Information">
        <div className="rdp-vbdm__body">
          <div className="rdp-vbdm__section">
            <div className="rdp-vbdm__grid">
              {bField("Request ID", request.id)}
              {bField("Status", request.status)}
              {bField("Submitted", request.formSubmittedDate ? fmt(request.formSubmittedDate) : null)}
              {bField("Requestor", request.requestedByName)}
              {bField("Requestor e-mail", request.requestedByEmail)}
              {bField("Line Manager", request.lineManagerToNotify)}
              {bField("Finance Approver", request.financeApproverToNotify)}
              {request.additionalEmail && bField("Additional email", request.additionalEmail)}
              {request.myHRId && bField("MyHR ID", request.myHRId)}
              {approval.approvedByLineManagerName && bField("Approved by Line Manager", approval.approvedByLineManagerName)}
              {approval.approvedByLineManagerAt && bField("Approved by Line Manager date", fmt(approval.approvedByLineManagerAt))}
              {approval.rejectedByLineManagerName && bField("Rejected by Line Manager", approval.rejectedByLineManagerName)}
              {approval.rejectedByLineManagerName && bField("Rejected by Line Manager date", fmt(approval.rejectedByLineManagerAt))}
              {approval.rejectedByLineManagerReason && bField("Line Manager rejection reason", approval.rejectedByLineManagerReason)}
              {approval.approvedByFinanceName && bField("Approved by Finance", approval.approvedByFinanceName)}
              {approval.approvedByFinanceAt && bField("Approved by Finance date", fmt(approval.approvedByFinanceAt))}
              {approval.rejectedByFinanceName && bField("Rejected by Finance", approval.rejectedByFinanceName)}
              {approval.rejectedByFinanceName && bField("Rejected by Finance date", fmt(approval.rejectedByFinanceAt))}
              {approval.rejectedByFinanceReason && bField("Finance rejection reason", approval.rejectedByFinanceReason)}
              {request.status === "Cancelled" && request.cancelledBy && bField("Cancelled By", request.cancelledBy)}
              {request.status === "Cancelled" && request.cancelledByReason && bField("Cancellation Reason", request.cancelledByReason)}
              {fileList.length > 0 && (
                <div className="rdp-vbdm__field">
                  <div className="rdp-vbdm__field-label">Attachments</div>
                  <div className="rdp-vbdm__field-value">
                    {fileList.map((file, i) => (
                      <a
                        key={i}
                        className="rdp-file-item"
                        href={file.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ display: "flex", marginBottom: 4 }}
                      >
                        <FontAwesomeIcon icon={faPaperclip} /> {file.displayName}
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </SectionCard>

      <SectionCard icon={faUniversity} title="Bank Details" badge={banks?.length ?? 0}>
        <div className="rdp-vbdm__body">
          {!banks?.length && (
            <div className="rdp-vbdm__section">
              <div className="rdp-vbdm__field-value rdp-vbdm__field-value--empty">No bank details</div>
            </div>
          )}
          {banks?.map((bankData, idx) => (
            <React.Fragment key={bankData.id ?? idx}>
              {idx > 0 && <div className="rdp-vbdm__divider" />}
              <div className="rdp-vbdm__section">
                <div className="rdp-vbdm__grid">{renderBankFields(bankData)}</div>
                {bankData?.files?.length > 0 && (
                  <div className="rdp-vbdm__grid">
                    <div className="rdp-vbdm__field">
                      <div className="rdp-vbdm__field-label">File</div>
                      <div className="rdp-vbdm__field-value">
                        {bankData.files.map((file, fi) => (
                          <a
                            key={fi}
                            className="rdp-file-item"
                            href={file.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ display: "flex", marginBottom: 4 }}
                          >
                            <FontAwesomeIcon icon={faFileAlt} /> {file.displayName}
                          </a>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </React.Fragment>
          ))}
        </div>
      </SectionCard>

      <SectionCard icon={faClock} title="MyHR Status">
        <div className="rdp-vbdm__body">
          <div className="rdp-vbdm__section">
            <div className="rdp-vbdm__grid">
              {bField("Status", myHRStatus?.status)}
              {bField("Error message", myHRStatus?.errors?.length ? myHRStatus.errors.join("; ") : myHRStatus?.errorMessage)}
              {bField("Retry count", myHRStatus ? String(myHRStatus.retryCount ?? 0) : null)}
              {bField("Received date", myHRStatus?.receivedAt ? fmt(myHRStatus.receivedAt) : null)}
              {bField("Resolved date", myHRStatus?.resolvedAt ? fmt(myHRStatus.resolvedAt) : null)}
              {bField("Next retry date", myHRStatus?.nextRetryAt ? fmt(myHRStatus.nextRetryAt) : null)}
            </div>
          </div>
        </div>
      </SectionCard>

      <SectionCard
        icon={faUpload}
        title="Add File Attachment"
        badge={fileList.length > 0 ? fileList.length : null}
      >
        <div className="rdp-file-attachments">
          <p className="rdp-upload-hint">Upload file as additional business justification.</p>
          <FilesForm
            bankDataId={0}
            requestId={request.id}
            name="mainFileForm"
            fileList={fileList}
            setFileList={setFileList}
            token={request.token}
            addAlert={addAlert}
          />
        </div>
      </SectionCard>

      <ResubmitModal
        addAlert={addAlert}
        setToggle={setToggleResubmitModal}
        toggle={toggleResubmitModal}
        onResubmit={onResubmit}
        requestId={id}
      />
      <RejectModal
        addAlert={addAlert}
        setToggle={setToggleRejectModal}
        toggle={toggleRejectModal}
        onReject={onReject}
        requestId={id}
      />
      <ApproveModal
        addAlert={addAlert}
        setToggle={setToggleApproveModal}
        toggle={toggleApproveModal}
        onSubmit={onApprove}
        requestId={id}
      />
    </div>
  );
};

export default FinanceDetailsPage;
